class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.button = this.querySelector('[data-collection-load-more-button]');
        this.count = this.querySelector('[data-collection-load-more-count]');
        this.sectionId = this.dataset.sectionId;
        this.nextUrl = this.dataset.nextUrl;
        this.shownCount = Number(this.dataset.shownCount || 0);
        this.totalCount = Number(this.dataset.totalCount || 0);
        this.isStaticLoadMore = this.hasAttribute('data-static-load-more');
        this.targetSelector = this.dataset.targetSelector;
        this.staticPageSize = Number(this.dataset.staticPageSize || 0);

        if (!this.button || (!this.isStaticLoadMore && (!this.sectionId || !this.nextUrl))) {
            this.hidden = true;
            return;
        }

        this.button.addEventListener('click', this.handleClick);

        if (this.isStaticLoadMore) {
            this.updateStaticState();
        }
    }

    disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleClick);
    }

    handleClick = async () => {
        if (this.isStaticLoadMore) {
            this.handleStaticClick();
            return;
        }

        if (!this.nextUrl || this.button?.disabled) return;

        const scrollYBeforeAppend = window.scrollY;
        const scrollXBeforeAppend = window.scrollX;

        this.setLoading(true);
        this.setScrollAnchoring(false);

        try {
            const loadedUrl = new URL(this.nextUrl, window.location.origin);
            const requestUrl = new URL(loadedUrl.toString());
            requestUrl.searchParams.set('section_id', this.sectionId);

            const response = await fetch(requestUrl.toString(), {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!response.ok) throw new Error(`Load more request failed: ${response.status}`);

            const html = await response.text();
            const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
            const nextGrid = parsedDocument.querySelector('[ref="grid"]');
            const currentResultsList = this.closest('results-list') || document;
            const currentGrid = currentResultsList.querySelector('[ref="grid"]');

            if (!nextGrid || !currentGrid) return;

            const currentPlaceholders = [...currentGrid.querySelectorAll(':scope > [data-product-grid-placeholder]')];
            const nextCards = [...nextGrid.querySelectorAll(':scope > [ref="cards[]"]')];
            const nextPlaceholders = [...nextGrid.querySelectorAll(':scope > [data-product-grid-placeholder]')];

            currentPlaceholders.forEach((placeholder) => placeholder.remove());
            currentGrid.append(...nextCards, ...nextPlaceholders);

            await this.waitForNextFrame();

            window.scrollTo({
                top: scrollYBeforeAppend,
                left: scrollXBeforeAppend,
                behavior: 'auto',
            });

            this.shownCount += nextCards.length;
            this.updateCount();

            const nextLoadMore = parsedDocument.querySelector('collection-load-more');
            const followingUrl = nextLoadMore?.dataset.nextUrl;

            if (followingUrl) {
                this.nextUrl = followingUrl;
                this.dataset.nextUrl = followingUrl;
            } else {
                this.hidden = true;
            }

            this.cleanCollectionUrl();

            await this.waitForImages(nextCards);
            await this.waitForStableGridHeight(currentGrid);
            await this.waitForNextFrame();
            await this.waitForNextFrame();

            document.dispatchEvent(
                new CustomEvent('norkaya:collection-grid-did-update', {
                    detail: {
                        source: 'load-more',
                        sectionId: this.sectionId,
                        scrollY: scrollYBeforeAppend,
                        scrollX: scrollXBeforeAppend,
                        cardsAdded: nextCards.length,
                    },
                })
            );
        } catch (error) {
            console.error(error);
        } finally {
            this.setScrollAnchoring(true);
            this.setLoading(false);
        }
    };

    handleStaticClick = async () => {
        if (this.button?.disabled) return;

        const staticItems = this.getStaticItems();
        const hiddenItems = staticItems.filter((item) => item.hidden);

        if (!hiddenItems.length) {
            this.updateStaticState();
            return;
        }

        const scrollYBeforeAppend = window.scrollY;
        const scrollXBeforeAppend = window.scrollX;
        const pageSize = this.getStaticPageSize();
        const nextItems = hiddenItems.slice(0, pageSize);

        this.setLoading(true);
        this.setScrollAnchoring(false);

        try {
            nextItems.forEach((item) => {
                item.hidden = false;
            });

            await this.waitForNextFrame();

            window.scrollTo({
                top: scrollYBeforeAppend,
                left: scrollXBeforeAppend,
                behavior: 'auto',
            });

            this.updateStaticState();

            await this.waitForImages(nextItems);
            await this.waitForStableGridHeight(this.getStaticGrid());
            await this.waitForNextFrame();

            document.dispatchEvent(
                new CustomEvent('norkaya:collection-grid-did-update', {
                    detail: {
                        source: 'static-load-more',
                        cardsAdded: nextItems.length,
                    },
                })
            );
        } finally {
            this.setScrollAnchoring(true);
            this.setLoading(false);
        }
    };

    getStaticPageSize() {
        this.staticPageSize = Number(this.dataset.staticPageSize || this.staticPageSize || 0);

        return Math.max(this.staticPageSize, 1);
    }

    getStaticGrid() {
        if (!this.targetSelector) return null;

        const scope = this.closest('[data-wishlist-page]') || this.parentElement || document;

        return scope.querySelector(this.targetSelector);
    }

    getStaticItems() {
        const grid = this.getStaticGrid();

        if (!grid) return [];

        return [...grid.querySelectorAll(':scope > [data-load-more-item]')];
    }

    updateStaticState() {
        if (!this.isStaticLoadMore) return;

        const staticItems = this.getStaticItems();
        const totalCount = staticItems.length;
        const shownCount = staticItems.filter((item) => !item.hidden).length;

        this.shownCount = shownCount;
        this.totalCount = totalCount;
        this.dataset.shownCount = String(shownCount);
        this.dataset.totalCount = String(totalCount);

        this.updateCount();
        this.hidden = !totalCount || shownCount >= totalCount;
    }

    setScrollAnchoring(isEnabled) {
        document.documentElement.style.overflowAnchor = isEnabled ? '' : 'none';
        document.body.style.overflowAnchor = isEnabled ? '' : 'none';
    }

    waitForImages(nodes) {
        const images = nodes
            .flatMap((node) => [...node.querySelectorAll('img')])
            .filter((image) => !image.complete);

        if (!images.length) return Promise.resolve();

        return new Promise((resolve) => {
            let resolvedImages = 0;

            const done = () => {
                resolvedImages += 1;

                if (resolvedImages >= images.length) {
                    resolve();
                }
            };

            images.forEach((image) => {
                image.addEventListener('load', done, { once: true });
                image.addEventListener('error', done, { once: true });
            });

            window.setTimeout(resolve, 900);
        });
    }

    waitForStableGridHeight(grid) {
        if (!grid) return Promise.resolve();

        return new Promise((resolve) => {
            let lastHeight = grid.getBoundingClientRect().height;
            let stableFrames = 0;
            let attempts = 0;

            const check = () => {
                const currentHeight = grid.getBoundingClientRect().height;
                const heightDiff = Math.abs(currentHeight - lastHeight);

                attempts += 1;

                if (heightDiff < 1) {
                    stableFrames += 1;
                } else {
                    stableFrames = 0;
                    lastHeight = currentHeight;
                }

                if (stableFrames >= 4 || attempts >= 40) {
                    resolve();
                    return;
                }

                requestAnimationFrame(check);
            };

            requestAnimationFrame(check);
        });
    }

    waitForNextFrame() {
        return new Promise((resolve) => {
            requestAnimationFrame(resolve);
        });
    }

    cleanCollectionUrl() {
        const currentUrl = new URL(window.location.href);

        currentUrl.searchParams.delete('page');
        currentUrl.searchParams.delete('section_id');
        history.replaceState({}, '', currentUrl.toString());
    }

    setLoading(isLoading) {
        if (!this.button) return;

        this.button.disabled = isLoading;
        this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    updateCount() {
        if (!this.count) return;

        this.count.textContent = `Shows ${this.shownCount} / ${this.totalCount} products`;
    }
}

if (!customElements.get('collection-load-more')) {
    customElements.define('collection-load-more', CollectionLoadMore);
}